import React from 'react';

import {
  FormPageWrapperProps,
  PageToolbar,
  PageToolbarProps,
  DrawerFilter,
} from '../../../../../base/presentation/components';
import { EnumStatusData } from '../../../../../base/domain/entities';
import {
  notificationFailed,
  notificationSuccess,
} from '../../../../../base/infrastructure/helpers';
import {
  useCurrentRoute,
  usePageProvider,
} from '../../../../../base/presentation/hooks';
import {
  DataTableContextEntity,
  DataTableProvider,
} from '../../../../../base/presentation/providers';
import { Form, Modal, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { PageWrapper } from './page-wrapper';
import { LoadingOutlined } from '@ant-design/icons';
import { omit } from 'lodash';

export function FormPageWrapper(props: FormPageWrapperProps) {
  const {
    children,
    padding,
    pageToolbarProps,
    dataDummy,
    forceRequest,
    ignoreKeyUpdate,
    ignoreKeyDuplicate,
    handleAfterCreate,
    handleAfterUpdate,
    getFormInstance,
    onChangeFormValues,
    formInstance,
    paramGetData,
    autoConfirmAfterAction = false,
    showPageHeader = true,
  } = props;

  const [form] = Form.useForm();
  const [showContent, setShowContent] = useState(true);
  const [dataType, setDataType] = useState<'original' | 'requested'>(
    'original'
  );
  const {
    action,
    defaultDetailData,
    detailData,
    setDetailData,
    id,
    moduleKey,
    isCreate,
    isUpdate,
    isDuplicate,
  } = usePageProvider();
  const { navigation, dataSource, transformer } = usePageProvider();
  const { transformerGetData } = transformer;
  const [currentRoute] = useCurrentRoute();

  useEffect(() => {
    if (getFormInstance) getFormInstance(form);
  }, [form]);

  function makeDataForm(payload: any, dataType = 'requested'): void {
    let newPayload = {};
    if (dataType === 'requested')
      newPayload = { ...payload.requested_data, id: payload.id };
    else newPayload = payload;
    let formPayload = dataDummy ?? newPayload;
    if (isUpdate) formPayload = omit(formPayload, ignoreKeyUpdate);
    else if (isDuplicate) formPayload = omit(formPayload, ignoreKeyDuplicate);
    form.setFieldsValue({ form_action: action, ...(formPayload ?? {}) });
    setDetailData(payload);
  }

  function transformerPayload(payload: any): void {
    let resultPayload = payload;
    const dataType = resultPayload.requested_data ? 'requested' : 'original';
    setDataType(dataType);
    if (transformerGetData) resultPayload = transformerGetData(payload);
    makeDataForm(resultPayload, dataType);
    setDetailData(resultPayload);
  }

  function handleChangeDataType(value: any): void {
    setDataType(value);
    makeDataForm(detailData, value);
  }

  async function handleGetData(): Promise<void> {
    await dataSource.handleGetData(id, {
      params: paramGetData,
      onSuccess: ({ response }: any) => {
        transformerPayload(response);
      },
      onFailed: ({ message }: any) => {
        notificationFailed(message);
      },
    });
  }

  function requestScheme(showData): void {
    if (dataDummy) transformerPayload(dataDummy);
    else if ((forceRequest || !showData) && id) handleGetData();
    else if (showData) transformerPayload(showData);
    else form.setFieldsValue({ form_action: action });
  }

  useEffect(() => {
    requestScheme(defaultDetailData);
  }, [forceRequest, defaultDetailData, id, dataDummy]);

  function transformerCreate(payload: any) {
    if (transformer?.transformerCreate)
      return transformer.transformerCreate(payload);
    return payload;
  }

  function transformerUpdate(payload: any) {
    if (transformer?.transformerUpdate)
      return transformer.transformerUpdate(payload);
    return payload;
  }

  function transformerDuplicate(payload: any) {
    if (transformer?.transformerDuplicate)
      return transformer.transformerDuplicate(payload);
    return payload;
  }

  async function handleConfirmProcess(payload): Promise<void> {
    await dataSource.handleConfirmProcess(payload, {
      onSuccess: ({ response }: any) => {
        navigation.goToDetail({ id: response.id });
      },
      onFailed: ({ message }: any) => {
        notificationFailed(message);
      },
    });
  }

  async function create(payload: any): Promise<void> {
    await dataSource.handleCreate(payload, {
      onSuccess: ({ response }: any) => {
        if (handleAfterCreate) handleAfterCreate(response);
        else if (autoConfirmAfterAction) handleConfirmProcess(response);
        else navigation.goToDetail({ id: response.id });
        notificationSuccess(['Successfully created data.']);
      },
      onFailed: ({ message }: any) => {
        notificationFailed(message);
      },
    });
  }

  async function update(payload: any): Promise<void> {
    await dataSource.handleUpdate(id, payload, {
      onSuccess: ({ response }: any) => {
        if (handleAfterUpdate) handleAfterUpdate(response);
        else if (autoConfirmAfterAction) handleConfirmProcess(response);
        else navigation.goToDetail({ id: response.id });
        notificationSuccess(['Successfully updated data.']);
      },
      onFailed: ({ message }: any) => {
        notificationFailed(message);
      },
    });
  }

  async function handleSave(): Promise<void> {
    try {
      const values = await form.validateFields();
      let payload = values;
      if (isUpdate) payload = transformerUpdate(payload);
      else if (isCreate) payload = transformerCreate(payload);
      else if (isDuplicate) payload = transformerDuplicate(payload);

      const cleanVal = omit(payload, ['id', 'form_action']);
      if (isUpdate) await update(cleanVal);
      else await create(cleanVal);
    } catch (error) {
      console.log(error);
    }
  }

  async function onChangeForm(item, values) {
    let newValues = undefined;
    if (onChangeFormValues) newValues = await onChangeFormValues(item, values);
    if (newValues) form.setFieldsValue(newValues);
  }

  function handleClickBack() {
    navigation.goBack(currentRoute);
  }

  async function handleClickReload(): Promise<void> {
    setShowContent(false);
    await new Promise(resolve => setTimeout(resolve, 500));
    setShowContent(true);
  }

  async function handleClickSave(): Promise<void> {
    try {
      if (form) await form.validateFields();
      Modal.confirm({
        mask: true,
        okText: 'Confirm',
        title: 'Save Confirmation',
        content: 'Are you sure you want to save this data?',
        onOk: () => handleSave(),
      });
    } catch (error) {}
  }

  const PageToolbarProperty: PageToolbarProps = {
    rowData: detailData,
    showUpdate: false,
    showDuplicate: false,
    showDelete: false,
    showDataTypeSelector: !!detailData?.requested_data,
    showApproval: false,
    showSearch: false,
    showActivate: false,
    showDeactivate: false,
    showExport: false,
    showImport: false,
    showPrint: false,
    showNewData: false,
    showFilter: false,
    showConfirmProcess: false,
    dataTypeValue: dataType,

    handleClickBack,
    handleClickReload,
    handleClickSave,
    handleChangeDataType,
    ...pageToolbarProps,
  };

  return (
    <div>
      {showPageHeader && <PageToolbar {...PageToolbarProperty} />}
      <PageWrapper padding={padding}>
        <Spin
          spinning={!showContent}
          indicator={<LoadingOutlined />}
          style={{ minHeight: '50vh' }}
        >
          <Form
            layout="vertical"
            form={form}
            name={`form-detail-${moduleKey}`}
            onValuesChange={onChangeForm}
            {...(formInstance ?? {})}
          >
            <Form.Item noStyle hidden name="id"></Form.Item>
            <Form.Item noStyle hidden name="detail_data"></Form.Item>
            {showContent && children}
          </Form>
        </Spin>
      </PageWrapper>
    </div>
  );
}
